<template>
<div id="calculator" v-bind:class="{ 'open' : showDraw }">
  <div 
    id="calculator-toggle"
    class="ff ff-center" 
    v-on:click="toggleDrawState"
  >
    <icon name="icon-calculator" color="white" size=30 />
    <icon name="ui-cross" color="white" size=30 />
    <transition name="fade">
      <span v-if="totalEstimates > 0" class="calc-total ff ff-center bg-blue">{{ totalEstimates }}</span>
    </transition>
  </div>
  
  <transition name="message">
    <div
      v-if="message"
      id="calculator-message"
      class="ff ff-align-between ff-valign-center ff-nowrap bg-white calc-message"
      :class="message.type"
    >
      <div class="calc-message-icon ff ff-align-right ff-valign-center">
        <icon :name="message.icon" :color="message.color" size=20 />
      </div>
      <div class="calc-message-text" v-html="message.text"></div>
      <div class="calc-message-arrow bg-grey ff ff-center">
        <icon name="ui-chevron-right" color="white" size=18 />
      </div>
    </div>
  </transition>
  
  <div 
    id="calculator-draw" 
    class="ff ff-ttb ff-nowrap ff-align-between"
  >
    
    <div class="calc-header ff ff-ttb ff-center">
      <div class="calc-header-title ff ff-center ff-nowrap">
        <icon name="icon-calculator" color="white" size=20 />
        <h5 class="white">Estimates</h5>
        <transition name="fade">
          <span v-if="totalEstimates > 0" class="calc-total ff ff-center bg-cyan">{{ totalEstimates }}</span>
        </transition>
      </div>
      <transition-group name="fade-list" tag="ul" class="calc-pager ff ff-center ff-nowrap ff-hide ff-sm-hide">
        <li v-for="(pager, p) in calcPager" v-on:click="goToEstimate(p)" :key="'pager-' + pager.uid">
          <span class="pager-circle" :class="totalEstimates > 0 && currentEstimate === p ? 'active' : ''"></span>
        </li>
      </transition-group>
    </div>
    
    <div class="calc-nav ff ff-align-between ff-valign-center ff-nowrap border-top-thin border-cyan">
      <div class="calc-arrow ff ff-center" v-on:click="goToEstimate('prev')">
        <icon name="ui-chevron-left" color="white" size=18 />
      </div>
      
      <transition name="fade" mode="out-in">
        <div class="calc-estimate-title" :key="'est-title-'+calcPager[currentEstimate].uid">
          <span class="white">
            {{ calcPager[currentEstimate].name }}
            <br v-if="calcPager[currentEstimate].estimateNo">
            <small v-if="calcPager[currentEstimate].estimateNo"><strong>Estimate:</strong> #{{ calcPager[currentEstimate].estimateNo }}</small>
          </span>
        </div>
      </transition>
      
      <div class="calc-arrow ff ff-center" v-on:click="goToEstimate('next')">
        <icon name="ui-chevron-right" color="white" size=18 />
      </div>
    </div>
    
    <div class="calc-scroller ff-fill">
      <transition :name="transitionName" mode="out-in">
        <div class="ff ff-ttb" :key="'estimate-tests-'+calcPager[currentEstimate].uid">
          <div id="calc-tests-list">
            <transition-group 
              name="fade-list" 
              v-on:before-leave="beforeListLeave"
              v-on:after-leave="afterListLeave"
              v-on:after-enter="afterListEnter"
              class="calc-tests"
              id="calc-tests" 
              tag="div"
            >
              <div 
                v-for="(test, t) in optimalTests" 
                class="calc-test ff-12" 
                :class="test.test_type | testClass"
                :key="'est-test-'+t+'-'+test.index"
              >
                <div class="calc-test-padding ff ff-12 ff-align-between ff-valign-center ff-nowrap">
                  <div class="calc-test-content ff-fill">

                    <div v-if="test.test_type === 0" class="calc-test-title">
                      <strong>Package:</strong> <span v-html="test.name"></span>
                    </div>

                    <div v-if="test.test_type === 1" class="calc-test-title">
                      <strong>Combination:</strong> <span v-html="test.name"></span>
                    </div>

                    <div v-if="test.test_type === 2" class="calc-test-title">
                      <strong v-html="test.name"></strong>
                    </div>

                    <div v-if="test.test_type === 0 && test.short_description" class="calc-test-desc">
                      <span>{{ test.short_description }}</span>
                    </div>

                    <hr class="hr-50" />

                    <div class="calc-test-price">
                      <span>${{ test.price }}</span> <s v-if="childTests[t] && !isSameAsOriginalPrice(test.price, childTests[t])">${{ childTests[t] | originalPrice }}</s> <em>(ex GST)</em>
                    </div>

                  </div>

                  <div class="calc-test-actions">

                    <div class="calc-test-action-delete ff ff-center" v-on:click="deleteTest(test.index)">
                      <icon name="ui-cross" color="white" size=18 />
                    </div>

                    <div 
                      v-if="test.test_type < 2 && test.children.length > 0" 
                      v-on:click="showChildTests(test.index,currentEstimate)" 
                      class="calc-test-action-toggle-children ff ff-center"
                      :class="parentTest.test === test.index && parentTest.estimate === currentEstimate ? 'open' : ''"
                    >
                      <icon name="ui-chevron-down" color="white" size=18 />
                    </div>

                    <router-link 
                      v-if="( test.test_type === 2 || ( test.test_type === 0 && test.children.length === 0 ) ) && test.url !== currentRoute" 
                      :to="test.url" 
                      class="calc-test-action-info ff ff-center"
                    >
                      <icon name="ui-info" color="grey" size=18 />
                    </router-link>

                    <div 
                      v-if="( test.test_type === 2 || ( test.test_type === 0 && test.children.length === 0 ) ) && test.url === currentRoute" 
                      v-on:click="toggleDrawState" 
                      class="calc-test-action-info ff ff-center"
                    >
                      <icon name="ui-info" color="grey" size=18 />
                    </div>

                  </div>

                  <div v-if="test.test_type < 2" class="calc-tag">
                    <icon name="icon-star-full" color="white" size=14 />
                  </div>
                </div>
                
                <transition name="slide-fade">
                  <div v-if="parentTest.test === test.index && parentTest.estimate === currentEstimate" class="calc-child-tests">
                    <div 
                      v-for="(childTest, c) in childTests[t]" 
                      class="calc-test ff-12"
                      :key="'est-child-' + c + '-' + childTest.index"
                    >
                      <div class="calc-test-padding ff ff-12 ff-align-between ff-valign-center ff-nowrap">
                        <div class="calc-test-content ff-fill">
                          <div class="calc-test-title">
                            <strong v-html="childTest.name"></strong>
                          </div>
                          <hr class="hr-50" />
                          <div class="calc-test-price">
                            <s><span>${{ childTest.price }}</span> <em>(ex GST)</em></s>
                          </div>
                        </div>
                        <div class="calc-test-actions">
                          <div class="calc-test-action-delete ff ff-center" v-on:click="deleteTest(childTest.index)">
                            <icon name="ui-cross" color="white" size=18 />
                          </div>
                          <router-link v-if="childTest.url !== currentRoute" :to="childTest.url" class="calc-test-action-info ff ff-center">
                            <icon name="ui-info" color="grey" size=18 />
                          </router-link>
                          <div v-else v-on:click="toggleDrawState" class="calc-test-action-info ff ff-center">
                            <icon name="ui-info" color="grey" size=18 />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </transition>
                
              </div>
              
              <div 
                v-for="(test, t) in dependentTests.list" 
                class="calc-test ff-12 test-type-option" 
                :class="test.test_type | testClass"
                :key="'est-dependent-'+t+'-'+test.index"
              >
                <div class="calc-test-padding ff ff-12 ff-align-between ff-valign-center ff-nowrap">
                  <div class="calc-test-option-text ff-fill">
                    <span class="calc-test-title" v-html="test.name"></span> <transition name="fade"><span v-if="dependentTests.inPackage.indexOf(test.index) === -1" class="calc-test-price ff-auto blue">+${{ test.price }} <em>(ex GST)</em></span></transition>
                  </div>
                  <div 
                    class="calc-test-option-toggle ff ff-center ff-nowrap bg-blue"
                    :class="dependentTests.active.indexOf(test.index) !== -1 ? 'active' : 'inactive'"
                  >
                    <div class="calc-test-option calc-test-option-true ff ff-center" v-on:click="addTest(test.index)"><h5>Yes</h5></div>
                    <div class="calc-test-option calc-test-option-false ff ff-center" v-on:click="deleteTest(test.index)"><h5>No</h5></div>
                  </div>
                </div>
              </div>
            </transition-group>
          </div>
          
          <div class="calc-padding">
            <transition name="slide-fade">
              <div v-if="optimalTests.length > 0" class="calc-totals ff ff-ttb">
                <h5 class="white calc-totals-title bg-cyan">Total</h5>
                <div class="calc-totals-price ff ff-align-between ff-valign-center ff-nowrap">
                  <div class="calc-totals-key">
                    Price per sample
                  </div>
                  <div class="calc-totals-value">
                    ${{ calcPager[currentEstimate].price }} <em>(ex GST)</em>
                  </div>
                </div>
                <div class="calc-totals-qty ff ff-align-between ff-valign-center ff-nowrap">
                  <div class="calc-totals-key">
                    Total samples
                  </div>
                  <div class="calc-totals-value">
                    <div class="calc-totals-qty-selector ff ff-center ff-nowrap bg-blue white">
                      <div class="calc-totals-qty-btn" 
                        :class="calcPager[currentEstimate].qty === 1 ? 'min' : ''" 
                        v-on:click="updateQty((parseInt(calcPager[currentEstimate].qty) - 1),currentEstimate)"
                      >
                        <icon name="ui-minus" color="cyan" size=20 />
                      </div>
                      
                      <div class="calc-totals-qty-total">
                        <input 
                          :value="calcPager[currentEstimate].qty" 
                          @keydown="numericOnly"
                          @input="updateQty($event.target.value,currentEstimate)" 
                          @blur="checkQty($event.target.value,currentEstimate)"
                          type="text"  
                          name="sample-qty" 
                          id="sample-qty" 
                          ref="sampleQty"
                        >
                      </div>
                      
                      <div 
                        class="calc-totals-qty-btn" 
                        v-on:click="updateQty((parseInt(calcPager[currentEstimate].qty) + 1),currentEstimate)"
                      >
                        <icon name="ui-plus" color="cyan" size=20 />
                      </div>
                    </div>
                  </div>
                </div>
                <div class="calc-totals-tally ff ff-ttb ff-center">
                  <div class="calc-totals-tally-total white">
                    ${{ calcPager[currentEstimate].total }}
                  </div>
                  <div class="calc-totals-tally-qty white">
                    <em class="white">(ex GST)</em>
                    <br>
                    <strong>${{ (calcPager[currentEstimate].total*1.1).toFixed(2) }} <em>(inc GST)</em></strong>
                    <br>
                    <span v-if="calcPager[currentEstimate].qty === 1" class="blue">for 1 sample</span>
                    <span v-else class="blue">for {{ calcPager[currentEstimate].qty }} samples</span>
                  </div>
                  <div v-if="calcPager[currentEstimate].totalLoss" class="calc-totals-tally-loss ff ff-center">
                    <div><icon name="ui-warning" color="white" size=30 /></div>
                    <em>The minimum charge per sample is <strong>${{ $store.state.global.minimum_price.toFixed(2) }} (ex GST)</strong>.</em>
                  </div>
                </div>
              </div>
            </transition>
            
            <div v-if="optimalTests.length === 0 || calcPager[currentEstimate].type === 'New'" id="calc-new-estimate-actions">
              <transition 
                name="fade" 
                mode="out-in" 
                v-on:before-leave="beforeLeave"
                v-on:after-enter="afterEnter"
              >
                <div v-if="!showOtherTypes && optimalTests.length === 0 && calcPager[currentEstimate].type === 'New'" class="calc-sample-type-selector" :key="'new-est-0'">
                  <p class="white ff-text-center">Select a <strong>Sample Type</strong> before adding to this estimate:</p>
                  <hr class="hr-50">
                  <div class="calc-actions ff ff-ttb">
                    <div 
                      v-for="(sampleType, s) in $store.state.sampleTypes" 
                      v-on:click="updateEstimate({estimate: currentEstimate, sampleType: sampleType.name})"
                      class="btn bg-white ff ff-center" 
                      :key="'est-stype-' + s + '-' + currentEstimate"
                    >
                      <icon :name="'icon-' + sampleType.icon" color="cyan" size=20 />
                      <span>{{ sampleType.name }}</span>
                    </div>
                  </div>
                </div>
                
                <div v-if="showOtherTypes" class="calc-sample-type-selector" :key="'new-est-1'">
                  <p class="white ff-text-center"><strong>Please specify:</strong></p>
                  <hr class="hr-50">
                  <div class="calc-actions ff ff-ttb">
                    <div 
                      v-for="(sampleType, subS) in $store.getters.otherSampleTypes.sub_categories" 
                      v-on:click="updateEstimate({estimate: currentEstimate, sampleType: sampleType.name})"
                      class="btn bg-white ff ff-center" 
                      :key="'est-stype-' + subS + '-' + currentEstimate"
                    >
                      <span>{{ sampleType.name }}</span>
                    </div>
                  </div>
                </div>
                
                <div v-if="!showOtherTypes && optimalTests.length === 0 && calcPager[currentEstimate].type !== 'New' && calcPager[currentEstimate].type !== 'None'" class="calc-sample-type-selector" :key="'new-est-2'">
                  <p class="white ff-text-center">There are currently <strong>no tests</strong> in this estimate:</p>
                  <hr class="hr-50">
                  <div class="calc-actions ff ff-ttb">
                    <div 
                      v-on:click="goToTests(calcPager[currentEstimate].categoryLink)"
                      class="btn bg-white ff ff-center" 
                    >
                      <icon name="icon-beaker" color="cyan" size=20 />
                      <span>Add a test</span>
                    </div>
                  </div>
                </div>
                
                <div v-if="!showOtherTypes && calcPager[currentEstimate].type === 'None'" class="calc-sample-type-selector" :key="'new-est-2'">
                  <p class="white ff-text-center" style="padding-bottom: 2rem;">There are currently <strong>no estimates</strong>:</p>
                </div>                
              </transition>
            </div>
          
            <div class="calc-actions ff ff-ttb">
              <div class="btn bg-blue ff ff-center" v-on:click="createEstimate">
                <icon name="icon-add" color="cyan" size=20 />
                <span class="white">New Estimate</span>
              </div>
              
              <div class="btn bg-blue ff ff-center" v-on:click="deleteEstimate(currentEstimate)">
                <icon name="icon-bin" color="cyan" size=20 />
                <span class="white">Delete Estimate</span>
              </div>
            </div>
          </div>
            
        </div>
      </transition>
    </div>
    
    <div class="calc-proceed">
      <router-link to="/estimates/review" class="btn bg-black ff ff-center ff-nowrap" @click.native="trackProceed">
        <span>Proceed</span>
        <icon name="ui-chevron-right" color="white" size=18 />
      </router-link>
    </div>
    
  </div>
  
  <transition name="fade">
    <div 
      v-if="showDraw" 
      v-on:click="toggleDrawState" 
      id="calculator-overlay"
    ></div>
  </transition>
</div>
</template>

<script>
  export default {
    name: "Calculator",
    data() {
      return {
        transitionName: 'page-swipe',
        showOtherTypes: false,
        parentTest: false
      }
    },
    computed: {
      showDraw () {
        var showDraw = this.$store.state.calculator.showDraw;
        return showDraw;
      },
      currentRoute () {
        var currentRoute = this.$route.path;
        
        return currentRoute;
      },
      currentEstimate () {
        var currentEstimate = this.$store.state.calculator.currentEstimate;
        return currentEstimate;
      },
      totalEstimates () {
        var totalEstimates = this.$store.getters.totalEstimates;
        return totalEstimates;
      },
      message() {
        var message = false;
        
        var calc = this.$store.state.calculator;
        
        if(calc.showMessage && calc.message && calc.messageType) {
          
          var icon = 'ui-tick';
          var color = 'cyan';
          
          switch(calc.messageType) {
            case 'success':
              icon = 'ui-tick';
              color = 'cyan';
              break;
              
            case 'package':
              icon = 'icon-percent';
              color = 'purple';
              break;
              
            case 'combination':
              icon = 'icon-group';
              color = 'orange';
              break;
              
            case 'warning':
              icon = 'ui-warning';
              color = 'pink';
              break;
              
            default:
              icon = 'ui-tick';
              color = 'cyan';
              break;
          }
          
          message = {
            text: calc.message,
            type: calc.messageType,
            icon: icon,
            color: color
          };
        }
        
        return message;
      },
      calcPager () {
        var calcPager = [{
          uid: 'uid-0',
          name: 'No Estimates',
          estimateNo: null,
          type: 'None',
          categoryLink: '/tests',
          qty: false,
          price: false,
          total: false,
          totalSavings: false
        }];
        
        var estimates = this.$store.state.calculator.estimates;
        
        if(estimates.length > 0) {
          calcPager = [];
          
          for(var i = 0; i < estimates.length; i++) {
            var sampleType = this.$store.state.sampleTypes.filter(s => s.name === estimates[i].displayedType);
            var samplePrice = parseFloat(estimates[i].samplePrice);
            var categoryLink = sampleType.length > 0 ? '/tests/'+sampleType[0].slug : '';
            var totalSavings = false;
            var totalLoss = false;
            
            if(samplePrice < this.$store.state.global.minimum_price) {
              totalLoss = (this.$store.state.global.minimum_price - samplePrice).toFixed(2);
              samplePrice = this.$store.state.global.minimum_price;
            }
            
            if(!totalLoss && estimates[i].sampleSaving) {
              totalSavings = (estimates[i].sampleSaving * estimates[i].sampleQty).toFixed(2);
            }
            
            calcPager.push({
              uid: estimates[i].uid,
              name: estimates[i].displayedType + ' Estimate (' + (i+1) + ')',
              estimateNo: estimates[i].estimateNo,
              type: estimates[i].displayedType,
              categoryLink: categoryLink,
              qty: estimates[i].sampleQty,
              price: samplePrice.toFixed(2),
              total: (samplePrice * estimates[i].sampleQty).toFixed(2),
              totalSavings: totalSavings,
              totalLoss: totalLoss
            });
          }
        }
        
        return calcPager;
      },
      optimalTests () {
        var optimalTests = [];
        var calc = this.$store.state.calculator;
        
        if(calc.estimates.length > 0) {
          var estimate = calc.estimates[calc.currentEstimate];
          
          if(estimate.tests.optimal.length > 0) {
            optimalTests = estimate.tests.optimal.filter(t => t.test_type <= 2);
          }
        }
        
        return optimalTests;
      },
      childTests () {
        var childTests = [];
        var calc = this.$store.state.calculator;

        if(calc.estimates.length > 0) {
          var estimate = calc.estimates[calc.currentEstimate];
          
          if(estimate.tests.matches.length > 0) {
            childTests = estimate.tests.matches;
          }
        }
        
        return childTests;
      },
      dependentTests () {
        var dependentTests = {
          active: [],
          inPackage: [],
          list: []
        };
        var calc = this.$store.state.calculator;
        var tests = this.$store.state.tests;
        var added = [];
        
        if(calc.estimates.length > 0) {
          var estimate = calc.estimates[calc.currentEstimate];
          
          if(estimate.tests.individual.length > 0) {
            
            estimate.tests.individual.forEach(function(parentTest){
              
              if(parentTest.children.length > 0) {
                var childTests = tests.filter(c => parentTest.children.indexOf(c.test_id) !== -1);
                
                childTests.forEach(function(t){
                  
                  if(t.test_type === 3 && added.indexOf(t.index) === -1) {
                    
                    added.push(t.index);
                    dependentTests.list.push(t);
                    
                    if(estimate.tests.individual.some(i => i.index === t.index)) {
                      dependentTests.active.push(t.index);
                    }
                    
                  }
                  
                });
              }
              
            });
          }
          
          if(estimate.tests.optimal.length > 0) {
            estimate.tests.optimal.forEach(function(parentTest){
              if(parentTest.children.length > 0 && parentTest.test_type < 2) {
                var pChildTests = tests.filter(c => parentTest.children.indexOf(c.test_id) !== -1);
                
                pChildTests.forEach(function(t){
                  if(t.test_type === 3) {
                    if(estimate.tests.individual.some(i => i.index === t.index)) {
                      dependentTests.inPackage.push(t.index);
                    }
                  }
                });
                
              }
            });
          }
        }
        
        return dependentTests;
      }
    },
    methods: {
      beforeLeave(el) {
        // Attach previous router-view height to <section>
        var parentEl = document.getElementById("calc-new-estimate-actions");
        parentEl.style.height = parseFloat(getComputedStyle(el).height) + 'px';
      },
      afterEnter(el) {
        // Attach new router-view height to <section>
        var parentEl = document.getElementById("calc-new-estimate-actions");
        parentEl.style.height = parseFloat(getComputedStyle(el).height) + 'px';
        
        // Attach 'auto' height to <section> after transition is complete
        setTimeout(function(){
          parentEl.style.height = 'auto';
        }, 400);
      },
      beforeListLeave() {
        // Attach previous router-view height to <section>
        var parentEl = document.getElementById("calc-tests-list");
        var elGroup = document.getElementById("calc-tests");
        parentEl.style.height = parseFloat(getComputedStyle(elGroup).height) + 'px';
      },
      afterListLeave() {
        // Attach new router-view height to <section>
        var parentEl = document.getElementById("calc-tests-list");
        var elGroup = document.getElementById("calc-tests");
        parentEl.style.height = parseFloat(getComputedStyle(elGroup).height) + 'px';
        
        // Attach 'auto' height to <section> after transition is complete
        setTimeout(function(){
          parentEl.style.height = 'auto';
        }, 400);
      },
      afterListEnter() {
        // Attach new router-view height to <section>
        var parentEl = document.getElementById("calc-tests-list");
        var elGroup = document.getElementById("calc-tests");
        parentEl.style.height = parseFloat(getComputedStyle(elGroup).height) + 'px';
        
        // Attach 'auto' height to <section> after transition is complete
        setTimeout(function(){
          parentEl.style.height = 'auto';
        }, 400);
      },
      goToTests(value) {
        this.toggleDrawState();
        this.$router.push({ path: value });
      },
      deleteTest(testIndex) {
        var estimateIndex = this.$store.state.calculator.currentEstimate;
        
        this.$store.dispatch("deleteFromEstimate", {
          test: testIndex,
          estimate: estimateIndex
        });
      },
      addTest(testIndex) {
        var alreadyExists = false;
        var estimateIndex = this.$store.state.calculator.currentEstimate;
        
        // Make sure that the estimate doesn't have it yet.
        var tests = this.$store.state.calculator.estimates[estimateIndex].tests;
        
        if(tests.individual.some(t => t.index === testIndex) || tests.optimal.some(t => t.index === testIndex)) {
          alreadyExists = true;
        }
        
        if(!alreadyExists){
          this.$store.dispatch("addToEstimate", {
            test: testIndex,
            estimate: estimateIndex
          });
        }
      },
      toggleDrawState () {
        
        // Commit states
        this.$store.commit("forceSetScrollDirection","up");
        this.$store.commit("toggleDrawState");
        
        // Disable scroll
        var showDraw = this.$store.state.calculator.showDraw;
        
        if(showDraw) {
          document.body.style.position = 'fixed';
          document.body.style.top = '-'+this.$store.state.scroll.current+'px';
        } else {
          document.body.style.position = '';
          document.body.style.top = '';
          window.scrollTo(0, this.$store.state.scroll.current);
        }
        
      },
      goToEstimate (value)  {
        var cEstimate = this.$store.state.calculator.currentEstimate;
        
        // Change transition
        if (value === 'prev' || value < cEstimate) {
          this.transitionName = 'page-swipe-reverse';
        } else {
          this.transitionName = 'page-swipe';
        } 
        
        // Update current estimate visible
        this.$store.commit("setCurrentEstimate",value);
      },
      createEstimate () {
        // Update later
        this.$store.commit("createEstimate",{
          sampleType: 'New',
          displayedType: 'New'
        });
        
        var vm = this;
        
        setTimeout(function(){
          var lastEst = vm.$store.state.calculator.estimates.length - 1;
          vm.$store.commit("setCurrentEstimate",lastEst);
        }, 50);
        
        
        this.$gtm.trackEvent({
          'event': 'estimate',
          'action': 'click',
          'button-name': 'new estimate'
        });
      },
      deleteEstimate (currentEstimate) {
        // Track before deletion
        var estimate = this.$store.state.calculator.estimates[currentEstimate];
        var tests = '';
        
        for(var i = 0; i < estimate.tests.optimal.length; i++){
          var test = estimate.tests.optimal[i].name;
          tests += i === (estimate.tests.optimal.length - 1) ? test : test+', ';
        }
        
        this.$gtm.trackEvent({
          'event': 'estimate',
          'action': 'click',
          'button-name': 'delete estimate',
          'soil-estimate': tests,
          'soil-sample-price': estimate.samplePrice,
          'soil-total-samples': estimate.sampleQty
        });
        
        // Delete after tracking is complete
        if((currentEstimate-1) >= 0) {
          this.$store.commit("setCurrentEstimate",currentEstimate-1);
        } else {
          this.$store.commit("setCurrentEstimate",0);
        }
        
        var vm = this;
        
        setTimeout(function(){
          vm.$store.commit("deleteEstimate",currentEstimate);
        }, 401);
        
      },
      updateEstimate (value) {
        // If is anything other than 'Other'
        if(value.sampleType !== 'Other') {
          
          var displayType = value.sampleType;
          var displayTypes = ['Soil','Plant','Carbon (CFI)','Water'];
          
          if (displayTypes.indexOf(value.sampleType) === -1) {
            displayType = 'Other';
          }
          
          this.$store.commit("updateEstimate",{
            estimate: value.estimate,
            sampleType: value.sampleType,
            displayedType: displayType
          });
          
          this.showOtherTypes = false;
          
        } else {
          
          this.showOtherTypes = true;
          
        }
      },
      updateQty(newQty,estimate) {
        var currentQty = this.$store.state.calculator.estimates[estimate].sampleQty;
        
        if(newQty !== '') {
          
          if(isNaN(newQty)) {
          
            newQty = currentQty;
            
          } else {
            
            if(newQty < 1) {
              newQty = 1;
            }
            
          }
          
          this.$store.commit("updateEstimate",{
            estimate: estimate,
            sampleQty: parseInt(newQty)
          });
        }
          
      },
      checkQty(newQty,estimate) {
        if(!newQty) {
          this.$store.commit("updateEstimate",{
            estimate: estimate,
            sampleQty: 1
          });
          
          this.$refs.sampleQty.value = 1;
        }
      },
      showChildTests(test,estimate) {
        
        if(this.parentTest.test === test && this.parentTest.estimate === estimate) {
          this.parentTest = false;
        } else {
          this.parentTest = {
            test: test,
            estimate: estimate
          };
        }
        
      },
      numericOnly(e) {
        var charCode = (e.which) ? e.which : e.keyCode;
        
        if (charCode > 31 && (charCode < 48 || charCode > 57)) {
          e.preventDefault();
        }
      },
      trackProceed() {
        var currentEstimate = this.$store.state.calculator.currentEstimate;
        var estimate = this.$store.state.calculator.estimates[currentEstimate];
        var tests = '';
        
        for(var i = 0; i < estimate.tests.optimal.length; i++){
          var test = estimate.tests.optimal[i].name;
          tests += i === (estimate.tests.optimal.length - 1) ? test : test+', ';
        }
        
        this.$gtm.trackEvent({
          'event': 'estimate',
          'action': 'click',
          'button-name': 'proceed',
          'soil-estimate': tests,
          'soil-sample-price': estimate.samplePrice,
          'soil-total-samples': estimate.sampleQty
        });
      },
      isSameAsOriginalPrice(price, childTests) {
        var originalPrice = 0;
        
        childTests.forEach(function(t){
          originalPrice += parseFloat(t.price);
        });
        
        return parseFloat(price) === originalPrice ? true : false;
      }
    },
    filters: {
      testClass(val) {
        var testClass = '';
        
        switch(val) {
          case 0:
            testClass = 'package';
            break;
          case 1:
            testClass = 'combination';
            break;
          case 2:
            testClass = 'individual';
            break;
          case 3:
            testClass = 'option';
            break;
          default:
            testClass = 'individual';
        }
        
        return 'test-type-'+testClass;
      },
      truncate(value) {
        var str = '';
        var val = value.replace(/(<([^>]+)>)/ig,' ');
        
        if (val.length > 160) {
          str = val.substring(0,160) + '...';
        } else {
          str = val;
        }
        
        return str;
      },
      originalPrice(tests) {
        var originalPrice = 0;
        
        tests.forEach(function(t){
          originalPrice += parseFloat(t.price);
        });
        
        return originalPrice.toFixed(2);
      }
    }
  }
</script>